import React, { useState, useEffect } from 'react';
import {
  ClipboardList,
  Plus,
  Search,
  Eye,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { purchaseOrdersApi, productsApi } from '../../lib/api';
import { toast } from 'sonner';

const PurchaseOrders = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  
  const [formData, setFormData] = useState({
    supplier_name: '',
    items: [],
    notes: ''
  });
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, productsRes] = await Promise.all([
        purchaseOrdersApi.getAll(),
        productsApi.getAll({ active_only: false })
      ]);
      setOrders(ordersRes.data || []);
      setProducts(productsRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    if (!selectedProduct || !quantity || !unitPrice) return;
    
    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;

    setFormData({
      ...formData,
      items: [...formData.items, {
        product_id: product.id,
        product_name: product.name,
        quantity: parseInt(quantity),
        unit_price: parseFloat(unitPrice),
        unit: product.unit
      }]
    });
    
    setSelectedProduct('');
    setQuantity('1');
    setUnitPrice('');
  };

  const handleRemoveItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = subtotal * 0.1;
    return { subtotal, tax, total: subtotal + tax };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      await purchaseOrdersApi.create(formData);
      toast.success('Purchase order created successfully');
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Error creating PO:', err);
      toast.error(err.response?.data?.detail || 'Failed to create purchase order');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await purchaseOrdersApi.updateStatus(orderId, newStatus);
      toast.success('PO status updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_name: '',
      items: [],
      notes: ''
    });
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'status-draft',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled',
    };
    return classes[status] || 'status-draft';
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.supplier_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="space-y-6" data-testid="purchase-orders-loading">
        <div className="skeleton h-12 w-48 rounded-md"></div>
        <div className="skeleton h-96 rounded-md"></div>
      </div>
    );
  }

  const totals = calculateTotal();

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase Orders</h1>
          <p className="text-slate-600 mt-1">{orders.length} total POs</p>
        </div>
        <Button 
          className="bg-orange-600 hover:bg-orange-700 text-white"
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          data-testid="create-po-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create PO
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by PO number or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {filteredOrders.length === 0 ? (
            <div className="empty-state py-16">
              <ClipboardList className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No purchase orders found</h3>
              <p className="text-slate-500">Create your first purchase order.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO #</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow key={order.id} data-testid={`po-row-${order.id}`}>
                      <TableCell className="font-mono text-sm">{order.po_number}</TableCell>
                      <TableCell>{order.supplier_name}</TableCell>
                      <TableCell className="text-right font-mono">${order.total.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadgeClass(order.status)} capitalize`}>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(order.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8"
                            onClick={() => setViewOrder(order)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {order.status === 'draft' && (
                            <>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-8 w-8 text-green-600"
                                onClick={() => handleStatusChange(order.id, 'confirmed')}
                                title="Confirm PO"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-8 w-8 text-red-600"
                                onClick={() => handleStatusChange(order.id, 'cancelled')}
                                title="Cancel PO"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {order.status === 'confirmed' && (
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-8 w-8 text-blue-600"
                              onClick={() => handleStatusChange(order.id, 'delivered')}
                              title="Mark as Delivered"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create PO Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 py-4">
              <div>
                <Label className="form-label">Supplier Name</Label>
                <Input
                  value={formData.supplier_name}
                  onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                  placeholder="Enter supplier name"
                  required
                  data-testid="supplier-name-input"
                />
              </div>

              {/* Add Items */}
              <div>
                <Label className="form-label">Add Products</Label>
                <div className="flex gap-2">
                  <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-20"
                    placeholder="Qty"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={unitPrice}
                    onChange={(e) => setUnitPrice(e.target.value)}
                    className="w-24"
                    placeholder="Price"
                  />
                  <Button type="button" onClick={handleAddItem} variant="outline">
                    Add
                  </Button>
                </div>
              </div>

              {/* Order Items */}
              {formData.items.length > 0 && (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell className="text-right font-mono">{item.quantity} {item.unit}</TableCell>
                          <TableCell className="text-right font-mono">${item.unit_price.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-mono">
                            ${(item.quantity * item.unit_price).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button 
                              type="button"
                              size="icon" 
                              variant="ghost" 
                              className="h-6 w-6 text-red-600"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="bg-slate-50 p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="font-mono">${totals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tax (10%)</span>
                      <span className="font-mono">${totals.tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-base border-t border-slate-200 pt-2">
                      <span>Total</span>
                      <span className="font-mono">${totals.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label className="form-label">Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any special instructions..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-orange-600 hover:bg-orange-700"
                disabled={formData.items.length === 0}
                data-testid="submit-po-button"
              >
                Create PO
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View PO Modal */}
      <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>PO {viewOrder?.po_number}</DialogTitle>
          </DialogHeader>
          {viewOrder && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Supplier</span>
                  <p className="font-medium">{viewOrder.supplier_name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Date</span>
                  <p className="font-medium">{new Date(viewOrder.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="text-slate-500">Status</span>
                  <Badge className={`${getStatusBadgeClass(viewOrder.status)} capitalize mt-1`}>
                    {viewOrder.status}
                  </Badge>
                </div>
              </div>

              <div className="border border-slate-200 rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewOrder.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell className="text-right font-mono">{item.quantity}</TableCell>
                        <TableCell className="text-right font-mono">${item.unit_price.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono">
                          ${(item.quantity * item.unit_price).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="bg-slate-50 p-4 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="font-mono">${viewOrder.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax</span>
                    <span className="font-mono">${viewOrder.tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t border-slate-200 pt-2">
                    <span>Total</span>
                    <span className="font-mono">${viewOrder.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {viewOrder.notes && (
                <div>
                  <span className="text-slate-500 text-sm">Notes</span>
                  <p className="text-sm mt-1">{viewOrder.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;
